import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, Calendar, Users, Download, Settings, Activity } from 'lucide-react';

const Layout = () => {
  const location = useLocation();

  const navItems = [
    { path: '/import', icon: Upload, label: 'Import', description: 'Upload CSV Files' },
    { path: '/scheduler', icon: Calendar, label: 'Scheduler', description: 'Assign Crew' },
    { path: '/contractors', icon: Users, label: 'Contractors', description: 'Manage Database' },
    { path: '/export', icon: Download, label: 'Export', description: 'Generate Reports' },
  ];

  return (
    <div className="min-h-screen bg-ops-bg-primary tactical-grid">
      {/* Top Bar */}
      <div className="bg-ops-bg-secondary border-b border-ops-border">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-ops-gradient-start to-ops-gradient-end flex items-center justify-center">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-ops-text-primary">IQCrew Scheduler</h1>
                  <p className="text-xs text-ops-text-tertiary font-mono">v1.0.0 OPERATIONAL</p>
                </div>
              </div>
            </div>
            
            {/* Status Indicators */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <span className="status-online text-xs text-ops-text-secondary">System Status</span>
              </div>
              <div className="text-xs font-mono text-ops-text-tertiary">
                {new Date().toLocaleString('en-US', { 
                  hour12: false,
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Bar */}
        <div className="px-6 pb-3">
          <nav className="flex space-x-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="relative group"
                >
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`
                      flex items-center space-x-3 px-4 py-2 rounded-lg
                      transition-all duration-200 cyber-button
                      ${isActive 
                        ? 'bg-ops-bg-hover border border-ops-accent-primary text-ops-accent-primary' 
                        : 'bg-ops-bg-card border border-ops-border text-ops-text-secondary hover:text-ops-text-primary hover:border-ops-border-light'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <div className="text-left">
                      <p className="text-sm font-semibold">{item.label}</p>
                      <p className="text-xs opacity-70">{item.description}</p>
                    </div>
                  </motion.div>
                  
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-ops-gradient-start to-ops-gradient-end"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30
                      }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 p-6">
        <div className="max-w-[1800px] mx-auto">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <Outlet />
          </motion.div>
        </div>
      </main>

      {/* Scanner Line Effect */}
      <div className="scanner-line pointer-events-none"></div>
    </div>
  );
};

export default Layout;
